import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  createTheme,
  ThemeProvider,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  confirmSignup,
  resendConfirmationCode,
  autoSignIn,
  updatePassword,
  logoutUser,
} from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';
import logo from '../../assets/images/cacxi-white-logo.svg';
import Notifications from '../common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { Mail, ShieldCheck, KeyRound, Check, X } from 'lucide-react';

const NewClientLogin = () => {
  const [requestCode, setRequestCode] = useState(true);
  const [showCodeConfirm, setShowCodeConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
      sendReqCode: '',
      newPassword: '',
      confirmPassword: '',
      otp: Array(6).fill(''),
    },
  });
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const watchNewPassword = watch('newPassword');
  const watchConfirmPassword = watch('confirmPassword');
  const [timer, setTimer] = useState(60);
  const [showResendButton, setShowResendButton] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Light theme specifically for login
  const lightTheme = createTheme({
    palette: {
      mode: 'dark',
    },
  });

  // Password validation checks
  const passwordChecks = {
    hasUpper: /[A-Z]/.test(watchNewPassword),
    hasLower: /[a-z]/.test(watchNewPassword),
    hasNumber: /\d/.test(watchNewPassword),
    hasSpecial: /[!@#$%^&*()_\-+=<>?{}[\]~]/.test(watchNewPassword),
    hasMinLength: watchNewPassword?.length >= 8,
    passwordsMatch: watchNewPassword === watchConfirmPassword && !!watchConfirmPassword,
  };

  const passwordsValid =
    passwordChecks.hasUpper &&
    passwordChecks.hasLower &&
    passwordChecks.hasNumber &&
    passwordChecks.hasSpecial &&
    passwordChecks.hasMinLength &&
    passwordChecks.passwordsMatch;

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (isSubmitting) return;
    const target = e.target as HTMLInputElement;
    if (e.key === 'Backspace' && !target.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (target.value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (isSubmitting) return;
    const pasteData = e.clipboardData.getData('text');
    const otpValues = pasteData.split('').slice(0, inputRefs.current.length);
    otpValues.forEach((value, index) => {
      if (inputRefs.current[index]) {
        inputRefs.current[index].value = value;
        inputRefs.current[index]?.focus();
      }
    });
    inputRefs.current[otpValues.length - 1]?.focus();
  };

  // Timer logic
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setShowResendButton(true);
    }
  }, [timer]);

  const handleRequestCode = async (data: any) => {
    try {
      await dispatch(resendConfirmationCode(data.email)).unwrap();
      setRequestCode(false);
      setShowCodeConfirm(true);
      setNotification({
        open: true,
        message: 'Code sent to your email successfully',
        severity: SEVERITY.SUCCESS,
      });
      reset({ ...data });
    } catch (error: any) {
      setRequestCode(true);
      setShowCodeConfirm(false);
      setNotification({
        open: true,
        message: error.message,
        severity: SEVERITY.ERROR,
      });
    }
  };

  const resendRequestCode = async () => {
    setTimer(60);
    setShowResendButton(false);
    try {
      await dispatch(resendConfirmationCode(getValues('email'))).unwrap();
      setRequestCode(false);
      setShowCodeConfirm(true);
      setNotification({
        open: true,
        message: 'Code sent to your email successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch (error: any) {
      setRequestCode(true);
      setShowCodeConfirm(false);
      setNotification({
        open: true,
        message: error.message,
        severity: SEVERITY.ERROR,
      });
    }
  };

  const handleConfirmCode = async (data: any) => {
    setIsSubmitting(true);
    const otpValue = data.otp.join('');
    try {
      await dispatch(confirmSignup({ username: data.email, code: otpValue })).unwrap();

      setNotification({
        open: true,
        message: 'Code Confirmed Successfully',
        severity: SEVERITY.SUCCESS,
      });

      // Automatically sign in with a temporary password or follow your flow
      const autoSigninData = {
        email: data.email,
        password: import.meta.env.VITE_TEMP_PASSWORD || '',
      };
      await dispatch(autoSignIn(autoSigninData)).unwrap();
      setShowCodeConfirm(false);
      setShowChangePassword(true);
      reset({ ...data });
    } catch (error: any) {
      setNotification({
        open: true,
        message: error.message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = async (data: any) => {
    setIsSubmitting(true);
    try {
      data = {
        password: import.meta.env.VITE_TEMP_PASSWORD || '',
        newPassword: data.newPassword,
      };
      await dispatch(updatePassword(data)).unwrap();
      await dispatch(logoutUser()).unwrap();
      setNotification({
        open: true,
        message: 'Password changed Successfully!',
        severity: SEVERITY.SUCCESS,
      });
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error: any) {
      setNotification({
        open: true,
        message: error.message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current step number
  const getCurrentStep = () => {
    if (requestCode) return 1;
    if (showCodeConfirm) return 2;
    if (showChangePassword) return 3;
    return 1;
  };

  // Input styling matching LoginForm
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
  };

  // Password requirement chip component
  const PasswordRequirementChip = ({ valid, label }: { valid: boolean; label: string }) => (
    <Chip
      size="small"
      icon={valid ? <Check size={14} /> : <X size={14} />}
      label={label}
      sx={{
        fontSize: '0.7rem',
        height: '24px',
        backgroundColor: valid ? 'rgba(34, 197, 94, 0.15)' : 'rgba(255, 255, 255, 0.05)',
        color: valid ? '#4ade80' : 'rgba(255, 255, 255, 0.5)',
        border: valid ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
        '& .MuiChip-icon': {
          color: valid ? '#4ade80' : 'rgba(255, 255, 255, 0.4)',
        },
      }}
    />
  );

  return (
    <Box className="h-screen flex items-center justify-center font-sans login-bg">
      <div className="max-w-sm w-full px-4">
        <ThemeProvider theme={lightTheme}>
          <Box
            sx={{
              backgroundColor: '#21172B',
              border: '1px solid #291c35',
              borderRadius: '12px',
              px: 5,
              py: 4,
            }}
          >
            {/* Step 1: Email Entry */}
            {requestCode && (
              <>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <img className="text-white w-28 mb-6 mx-auto" src={logo} alt="CAC_logo" />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      mb: 2,
                    }}
                  >
                    Step {getCurrentStep()} of 3
                  </Typography>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(168, 85, 247, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <Mail size={28} color="#a855f7" />
                  </Box>
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Verify Your Account
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Enter your email and we'll send you a verification code
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit(handleRequestCode)}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Enter Valid Email',
                      },
                    })}
                    sx={inputSx}
                  />
                  {errors.email && (
                    <Typography sx={{ color: '#fca5a5', fontSize: '0.75rem', mt: 0.5 }}>
                      {errors.email.message}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!!errors.email}
                    className="mt-4 bg-purple-800 py-2 text-md text-white font-bold hover:bg-purple-900"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '12px',
                      py: 1.5,
                      mt: 3,
                    }}
                  >
                    Send Code
                  </Button>
                </form>
              </>
            )}

            {/* Step 2: OTP Verification */}
            {showCodeConfirm && (
              <>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <img className="text-white w-28 mb-6 mx-auto" src={logo} alt="CAC_logo" />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      mb: 2,
                    }}
                  >
                    Step {getCurrentStep()} of 3
                  </Typography>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(168, 85, 247, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <ShieldCheck size={28} color="#a855f7" />
                  </Box>
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Enter Verification Code
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    A 6-digit code was sent to your email
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit(handleConfirmCode)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    {Array.from({ length: 6 }, (_, index) => (
                      <TextField
                        key={index}
                        inputRef={el => (inputRefs.current[index] = el)}
                        inputProps={{
                          maxLength: 1,
                          style: { textAlign: 'center', fontSize: '1.25rem' },
                        }}
                        {...register(`otp.${index}`, {
                          required: 'Confirmation code cannot be empty',
                        })}
                        onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
                          handleKeyUp(e, index)
                        }
                        onPaste={handlePaste}
                        sx={{
                          width: '44px',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                            '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                          },
                          '& .MuiInputBase-input': { padding: '12px 8px' },
                        }}
                      />
                    ))}
                  </Box>

                  {/* Resend Timer */}
                  <Box sx={{ mt: 1.5, mb: 3 }}>
                    {showResendButton ? (
                      <Button
                        onClick={resendRequestCode}
                        sx={{
                          fontSize: '0.813rem',
                          color: '#a855f7',
                          textTransform: 'none',
                          padding: 0,
                          minWidth: 'auto',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: '#c084fc',
                          },
                        }}
                      >
                        Resend Code
                      </Button>
                    ) : (
                      <Typography sx={{ fontSize: '0.813rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                        Resend in {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
                      </Typography>
                    )}
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isSubmitting}
                    className="bg-purple-800 py-2 text-md text-white font-bold hover:bg-purple-900"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '12px',
                      py: 1.5,
                    }}
                  >
                    {isSubmitting ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </form>
              </>
            )}

            {/* Step 3: Password Change */}
            {showChangePassword && (
              <>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <img className="text-white w-28 mb-6 mx-auto" src={logo} alt="CAC_logo" />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      mb: 2,
                    }}
                  >
                    Step {getCurrentStep()} of 3
                  </Typography>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(168, 85, 247, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <KeyRound size={28} color="#a855f7" />
                  </Box>
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Create New Password
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Set a secure password for your account
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit(handlePasswordChange)}>
                  <Typography
                    sx={{
                      fontSize: '0.813rem',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 1,
                    }}
                  >
                    New Password
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter new password"
                    type="password"
                    {...register('newPassword', {
                      required: 'New Password is required',
                      pattern: {
                        value:
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=<>?{}[\]~]).{8,}$/,
                        message: 'Password does not meet requirements',
                      },
                    })}
                    sx={inputSx}
                  />
                  {errors.newPassword && (
                    <Typography sx={{ color: '#fca5a5', fontSize: '0.75rem', mt: 0.5 }}>
                      {errors.newPassword.message}
                    </Typography>
                  )}

                  {/* Password Requirements Chips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5, mb: 2 }}>
                    <PasswordRequirementChip valid={passwordChecks.hasUpper} label="Uppercase" />
                    <PasswordRequirementChip valid={passwordChecks.hasLower} label="Lowercase" />
                    <PasswordRequirementChip valid={passwordChecks.hasNumber} label="Number" />
                    <PasswordRequirementChip valid={passwordChecks.hasSpecial} label="Special" />
                    <PasswordRequirementChip valid={passwordChecks.hasMinLength} label="8+ chars" />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: '0.813rem',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 1,
                    }}
                  >
                    Confirm Password
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Confirm new password"
                    type="password"
                    {...register('confirmPassword', {
                      required: 'Confirm Password is required',
                      validate: value => value === watchNewPassword || 'Passwords do not match',
                    })}
                    sx={{ ...inputSx, mb: 1 }}
                  />
                  {errors.confirmPassword && (
                    <Typography sx={{ color: '#fca5a5', fontSize: '0.75rem', mt: 0.5 }}>
                      {errors.confirmPassword.message}
                    </Typography>
                  )}

                  {watchConfirmPassword && (
                    <Box sx={{ mb: 2 }}>
                      <PasswordRequirementChip
                        valid={passwordChecks.passwordsMatch}
                        label="Passwords match"
                      />
                    </Box>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!passwordsValid || isSubmitting}
                    className="bg-purple-800 py-2 text-md text-white font-bold hover:bg-purple-900"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '12px',
                      py: 1.5,
                      mt: 1,
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(168, 85, 247, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {isSubmitting ? 'Setting Password...' : 'Set Password'}
                  </Button>
                </form>
              </>
            )}
          </Box>
        </ThemeProvider>
      </div>

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default NewClientLogin;
